import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useEffect, useState } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { isPlanReviewBeforeSign, PlanReviewDialog } from 'partner360/features/planReview'
import { ConfirmDialog } from 'shared/components/confirmDialog'
import { NetworkDebugSource } from 'shared/lib/networkDebug'

import { CommitmentTypes } from '../model/enums'
import type { AcknowledgmentCommitmentItem, AcknowledgmentUpdatedPayload } from '../model/types'
import { useAcknowledgmentStatusDialog } from '../model/useAcknowledgmentStatusDialog'
import { ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS } from '../translations'

import { AcknowledgmentCommentDialog } from './AcknowledgmentCommentDialog'

export type AcknowledgmentSignFlowProps = {
  partnerId: string
  fYear: number
  offering: string
  /** The row to sign, of any commitment type; without one the reader's own PBM row is signed. */
  entryId?: number
  /** The flow is over: signed, refused or left. */
  onClose: () => void
  /** The row is not the reader's to sign, or the rows could not be read: the host opens the full
   *  acknowledgment window instead. */
  onUnavailable: () => void
  /** True while the rows are read and while the signature is sent, so the host's button can say
   *  that something is under way. */
  onBusyChange?: (isBusy: boolean) => void
  onUpdated?: (payload: AcknowledgmentUpdatedPayload) => void | Promise<void>
}

/* The row the server marks `SignoffPending`, as the cockpit's own button reads it: `CanSignOff`
   is not always recalculated, so it does not decide. */
const findRowToSign = (items: AcknowledgmentCommitmentItem[], entryId: number | undefined) =>
  items.find(
    ({ EntryId, CommitmentType, SignoffPending }) =>
      (SignoffPending ?? 0) > 0 &&
      (entryId === undefined ? CommitmentType === CommitmentTypes.PBM : EntryId === entryId)
  ) ?? null

/**
 * A signature without the acknowledgment window: the same plan review, the same partner warning
 * and the same `signOff` the window's own `Sign` runs, started straight from the host's button.
 * Mounted for one signature and unmounted by the host once `onClose` or `onUnavailable` is called.
 */
const AcknowledgmentSignFlowBody = ({
  partnerId,
  fYear,
  offering,
  entryId,
  onClose,
  onUnavailable,
  onBusyChange,
  onUpdated,
}: AcknowledgmentSignFlowProps) => {
  const [isRefusing, setIsRefusing] = useState(false)
  const [isWarning, setIsWarning] = useState(false)
  const [isReviewDone, setIsReviewDone] = useState(false)

  const { viewModel, partnerInfo, hasLoaded, error, isSubmitting, handleSign, handleRefuse } =
    useAcknowledgmentStatusDialog({ partnerId, fYear, programType: offering, onUpdated })

  const isReady = hasLoaded && partnerInfo !== null
  const item = isReady && !error ? findRowToSign(viewModel.items, entryId) : null
  /* Once signed the row is read back unsignable, which is not the reader lacking a row. */
  const isUnavailable = isReady && !item && !isReviewDone

  useEffect(() => {
    if (isUnavailable) {
      onUnavailable()
    }
  }, [isUnavailable, onUnavailable])

  const isBusy = !isReady || isSubmitting

  useEffect(() => {
    onBusyChange?.(isBusy)
  }, [isBusy, onBusyChange])

  useEffect(() => () => onBusyChange?.(false), [onBusyChange])

  const sign = async () => {
    if (item) {
      await handleSign(item)
    }

    onClose()
  }

  /* As in the window: the partner's side is warned first when the plan carries a warning. */
  const handleContinue = () => {
    setIsReviewDone(true)

    if (item?.CommitmentType === CommitmentTypes.Partner && partnerInfo?.warningMessage) {
      setIsWarning(true)
      return
    }

    void sign()
  }

  /* Up to FY26 there is no plan review: the row is signed as soon as it is read. */
  const skipsPlanReview = item !== null && !isReviewDone && !isPlanReviewBeforeSign(fYear)

  useEffect(() => {
    if (skipsPlanReview) {
      handleContinue()
    }
  })

  const handleReject = () => {
    setIsReviewDone(true)
    setIsRefusing(true)
  }

  const handleApplyRefuse = async (comment: string) => {
    if (item && (await handleRefuse(item, comment))) {
      onClose()
    }
  }

  if (!item) {
    return null
  }

  return (
    <>
      <PlanReviewDialog
        show={!isReviewDone && isPlanReviewBeforeSign(fYear)}
        partnerId={partnerId}
        fYear={fYear}
        programType={offering as ProgramTypes}
        isGlobal={partnerInfo?.isGlobal}
        partner={{
          name: partnerInfo?.partnerName,
          isDistributor: partnerInfo?.isDistributor,
        }}
        role={item.CommitmentType}
        continueCaption={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.common.sign}
        showSummary
        onClose={onClose}
        onContinue={handleContinue}
        onReject={handleReject}
      />

      <ConfirmDialog
        isVisible={isWarning}
        size="xs"
        title="jbp-commitment-warning-header"
        content={<Translation span text={partnerInfo?.warningMessage ?? ''} />}
        confirmBtnText="jbp-continue"
        rejectBtnText="partnercommitment-cancel"
        onConfirm={() => {
          setIsWarning(false)
          void sign()
        }}
        onReject={onClose}
      />

      <AcknowledgmentCommentDialog
        show={isRefusing}
        mode="refuse"
        isSubmitting={isSubmitting}
        onClose={onClose}
        onApply={handleApplyRefuse}
      />
    </>
  )
}

/* The source stands above the flow's own hooks, so its reads and the signature are recorded
   under it. */
export const AcknowledgmentSignFlow = (props: AcknowledgmentSignFlowProps) => (
  <NetworkDebugSource name="Acknowledge button">
    <AcknowledgmentSignFlowBody {...props} />
  </NetworkDebugSource>
)

AcknowledgmentSignFlow.displayName = 'AcknowledgmentSignFlow'
